import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Image as ImageIcon, X, Download } from "lucide-react";
import { api } from "../../lib/api";

interface Categoria {
  id: number;
  nombre: string;
  descripcion?: string;
  fotoUrl?: string;
  icono?: string;
  color?: string;
  orden: number;
  visibleMenu: boolean;
  visibleDelivery: boolean;
  activo: boolean;
  sucursalId?: number | null;
}

const VACIA: Partial<Categoria> = {
  nombre: "",
  descripcion: "",
  fotoUrl: "",
  visibleMenu: true,
  visibleDelivery: true,
  activo: true,
};

export function CategoriasPage() {
  const [lista, setLista] = useState<Categoria[]>([]);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState<Partial<Categoria> | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await api<Categoria[]>("/v1/categorias");
      setLista(r);
    } catch (e: any) {
      setError(e.message || "Error al cargar");
    } finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, []);

  const guardar = async () => {
    if (!editando?.nombre?.trim()) { setError("Nombre requerido"); return; }
    setGuardando(true); setError(null);
    try {
      if (editando.id) {
        await api(`/v1/categorias/${editando.id}`, { method: "PATCH", body: editando });
      } else {
        await api("/v1/categorias", { method: "POST", body: editando });
      }
      setEditando(null);
      await cargar();
    } catch (e: any) {
      setError(e.message || "Error al guardar");
    } finally { setGuardando(false); }
  };

  const eliminar = async (id: number) => {
    if (!confirm("¿Eliminar esta categoría? Los artículos quedarán sin categoría asignada.")) return;
    try {
      await api(`/v1/categorias/${id}`, { method: "DELETE" });
      await cargar();
    } catch (e: any) { alert(e.message); }
  };

  const mover = async (idx: number, dir: -1 | 1) => {
    const nuevo = [...lista];
    const j = idx + dir;
    if (j < 0 || j >= nuevo.length) return;
    [nuevo[idx], nuevo[j]] = [nuevo[j], nuevo[idx]];
    setLista(nuevo);
    await api("/v1/categorias/reordenar", { method: "PATCH", body: { ids: nuevo.map(c => c.id) } });
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Categorías</h1>
          <p className="text-sm text-gray-500 mt-1">
            Una categoría sirve para ambos: <b>menú digital</b> y <b>delivery</b>.
            Cada artículo decide si aparece en menú, delivery o ambos.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              if (!confirm("Importar familias del POS como categorías y asignar artículos automáticamente?")) return;
              try {
                const r: any = await api("/v1/categorias/importar-icg", { method: "POST", body: {} });
                alert(`Creadas: ${r.creadas}\nYa existían: ${r.omitidas}\nArtículos asignados: ${r.articulosAsignados}`);
                await cargar();
              } catch (e: any) { alert(e.message); }
            }}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl shadow-sm hover:bg-gray-50 transition"
          >
            <Download className="size-4" /> Importar del POS
          </button>
          <button
            onClick={() => setEditando({ ...VACIA })}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl shadow hover:bg-indigo-700 transition"
          >
            <Plus className="size-4" /> Nueva categoría
          </button>
        </div>
      </div>

      {cargando ? (
        <div className="text-center py-12 text-gray-400">Cargando…</div>
      ) : lista.length === 0 ? (
        <div className="text-center py-16 text-gray-400 bg-white rounded-2xl border border-dashed border-gray-200">
          <p className="text-5xl mb-3">📂</p>
          <p className="text-gray-600 font-medium">Aún no creaste categorías</p>
          <p className="text-sm mt-1">Comenzá creando "Entradas", "Pizzas", "Bebidas", etc.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {lista.map((c, idx) => (
            <div key={c.id}
              className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-4 hover:border-gray-300 transition">
              <div className="flex flex-col gap-1">
                <button disabled={idx === 0} onClick={() => mover(idx, -1)}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                  <ChevronUp className="size-4" />
                </button>
                <button disabled={idx === lista.length - 1} onClick={() => mover(idx, 1)}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30">
                  <ChevronDown className="size-4" />
                </button>
              </div>

              {c.fotoUrl ? (
                <img src={c.fotoUrl} alt={c.nombre}
                  className="w-16 h-16 rounded-xl object-cover border border-gray-200" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-400">
                  <ImageIcon className="size-7" />
                </div>
              )}

              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900">{c.nombre}</h3>
                {c.descripcion && (
                  <p className="text-xs text-gray-500 truncate mt-0.5">{c.descripcion}</p>
                )}
                <div className="flex gap-2 mt-2 text-xs">
                  {c.visibleMenu && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Menú
                    </span>
                  )}
                  {c.visibleDelivery && (
                    <span className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                      Delivery
                    </span>
                  )}
                  {!c.activo && (
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                      Inactiva
                    </span>
                  )}
                </div>
              </div>

              <button onClick={() => setEditando(c)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
                <Pencil className="size-4" />
              </button>
              <button onClick={() => eliminar(c.id)}
                className="p-2 rounded-lg hover:bg-red-50 text-red-500">
                <Trash2 className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal edición */}
      {editando && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">
                {editando.id ? "Editar categoría" : "Nueva categoría"}
              </h2>
              <button onClick={() => setEditando(null)} className="p-1 rounded hover:bg-gray-100">
                <X className="size-5 text-gray-500" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Nombre *
                </label>
                <input value={editando.nombre || ""}
                  onChange={(e) => setEditando({ ...editando, nombre: e.target.value })}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500"
                  placeholder="Ej: Pizzas, Hamburguesas, Postres…" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Descripción
                </label>
                <textarea value={editando.descripcion || ""}
                  onChange={(e) => setEditando({ ...editando, descripcion: e.target.value })}
                  rows={2}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500 resize-none"
                  placeholder="Breve descripción (opcional)" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  URL de foto
                </label>
                <input value={editando.fotoUrl || ""}
                  onChange={(e) => setEditando({ ...editando, fotoUrl: e.target.value })}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 focus:outline-none focus:border-indigo-500"
                  placeholder="https://… (opcional)" />
                {editando.fotoUrl && (
                  <img src={editando.fotoUrl} alt=""
                    className="mt-2 w-24 h-24 object-cover rounded-lg border border-gray-200" />
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-emerald-300 transition">
                  <input type="checkbox" checked={editando.visibleMenu ?? true}
                    onChange={(e) => setEditando({ ...editando, visibleMenu: e.target.checked })}
                    className="size-4 accent-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">Menú digital</span>
                </label>
                <label className="flex items-center gap-2 p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-orange-300 transition">
                  <input type="checkbox" checked={editando.visibleDelivery ?? true}
                    onChange={(e) => setEditando({ ...editando, visibleDelivery: e.target.checked })}
                    className="size-4 accent-orange-600" />
                  <span className="text-sm font-medium text-orange-700">Delivery</span>
                </label>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={editando.activo ?? true}
                  onChange={(e) => setEditando({ ...editando, activo: e.target.checked })}
                  className="size-4 accent-gray-600" />
                Categoría activa
              </label>

              {error && <p className="text-red-500 text-sm">{error}</p>}
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setEditando(null)}
                className="px-4 py-2 rounded-xl text-gray-600 hover:bg-gray-200 transition">
                Cancelar
              </button>
              <button onClick={guardar} disabled={guardando}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-50">
                {guardando ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
